import React, { useEffect, useState, useCallback, useMemo, Component } from 'react';
import PropTypes from 'prop-types';
import './Projects.css';
import ProjectTable from '../../components/Projects/ProjectTable';
import apiService from '../../services/api';
import { Box, Typography, Snackbar, Checkbox, LinearProgress, CircularProgress } from '@mui/material';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import AddProjectDialog from '../../components/Projects/AddProjectDialog';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import StatusPill from '../../components/Projects/StatusPill';
import EditDialog from '../../components/Projects/EditDialog';
import DeleteDialog from '../../components/Projects/DeleteDialog';
import SendDialog from '../../components/Projects/SendDialog';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSort, faSortUp, faSortDown, faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Pie, Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, LineElement, PointElement } from 'chart.js';
import Cookies from 'js-cookie';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ViewResponsePopup from '../../components/Surveys/ViewResponse';
import * as XLSX from 'xlsx';
import ResendDialog from '../../components/Projects/ResendDialog';

const unionSvg = apiService.getBlobAssetUrl('Add_Icon.svg');
const Surveysentsvg = apiService.getBlobAssetUrl('Survey_sent.svg');
const Completedsurveysvg = apiService.getBlobAssetUrl('Completed.svg');

// Register Chart.js components and plugins
ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, LineElement, PointElement, ChartDataLabels);
library.add(faSort, faSortUp, faSortDown, faAngleLeft, faAngleRight);
const userId = sessionStorage.getItem('user_id');

// Helper function to compare objects for deep equality
function areObjectsEqual(obj1, obj2) {
  if (!obj1 || !obj2) return false;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  return keys1.every((key) => obj1[key] === obj2[key]);
}

// Helper function for pagination range
function getPageRange(currentPage, totalPages, maxVisiblePages = 3) {
  const currentPageSafe = Math.max(1, Math.min(currentPage || 1, totalPages || 1));
  let startPage = Math.max(1, currentPageSafe - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages || 1, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  return { startPage, endPage };
}

// Error Boundary Component
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography color="error">
            Something went wrong. Please try refreshing the page or contact support.
          </Typography>
          <Typography variant="caption">{this.state.error?.message}</Typography>
        </Box>
      );
    }
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

// Helper component for pagination buttons
function Pagination({ totalPages, currentPage, onPageChange, startPage, endPage, onPrevious, onNext }) {
  return (
    <div className="Project-pagination">
      <button
        onClick={onPrevious}
        disabled={startPage === 1}
        className="Project-pagination-button"
      >
        <FontAwesomeIcon icon={faAngleLeft} />
      </button>
      {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
        const pageNumber = startPage + i;
        const isSelected = currentPage === pageNumber;
        const buttonClass = `Project-pagination-button ${isSelected ? 'Project-selected' : ''}`;
        return (
          <button
            key={`page-${pageNumber}`}
            onClick={() => onPageChange(pageNumber)}
            className={buttonClass}
          >
            {pageNumber}
          </button>
        );
      })}
      <button
        onClick={onNext}
        disabled={endPage >= totalPages}
        className="Project-pagination-button"
      >
        <FontAwesomeIcon icon={faAngleRight} />
      </button>
    </div>
  );
}

Pagination.propTypes = {
  totalPages: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  startPage: PropTypes.number.isRequired,
  endPage: PropTypes.number.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

// Helper function for calculating days difference
function calculateDaysDiff(start, end = null) {
  if (!start || start === 'N/A') {
    console.warn('sentDate is missing or invalid:', start);
    return 'N/A';
  }

  const startDate = new Date(start);
  if (isNaN(startDate.getTime())) {
    console.warn('Invalid sentDate format:', start);
    return 'N/A';
  }

  let endDate;
  if (end === 'N/A' || end === null) {
    endDate = new Date(); // Use current date if no response or 'N/A'
  } else {
    endDate = new Date(end);
    if (isNaN(endDate.getTime())) {
      console.warn('Invalid RespondedDate format:', end);
      return 'N/A';
    }
  }

  const diffTime = Math.abs(endDate - startDate);
  const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return days;
}
// Helper function to calculate ageing categories
function calculateAgeingDistribution(surveys) {
  return surveys.reduce(
    (acc, survey) => {
      const days = calculateDaysDiff(survey.sentDate, survey.RespondedDate);
      // Skip surveys with non-numeric or invalid ageing values
      if (days === 'N/A') {
        console.warn('Invalid days ageing for survey:', survey.sentDate, survey.RespondedDate);
        return acc;
      }
      const daysNum = parseInt(days);
      if (isNaN(daysNum)) {
        console.warn('Invalid days ageing value for survey:', survey.sentDate, days);
        return acc;
      }
      // Categorize based on days
      if (daysNum < 15) {
        acc.lessThan15 += 1;
      } else if (daysNum >= 15 && daysNum <= 30) {
        acc.between15and30 += 1;
      } else if (daysNum > 30) {
        acc.greaterThan30 += 1;
      }
      return acc;
    },
    { lessThan15: 0, between15and30: 0, greaterThan30: 0 }
  );
}

// Helper component for Days Since Sent rendering
function DaysSinceSentCell({ responseStatus, sentDate, counter, calculateDaysSinceSent }) {
  const result = calculateDaysSinceSent(sentDate, counter, responseStatus);
  if (result === 'Survey Expired') {
    return <span className="Project-survey-expired">Survey Expired</span>;
  }
  return <span>{result}</span>;
}

DaysSinceSentCell.propTypes = {
  responseStatus: PropTypes.string.isRequired,
  sentDate: PropTypes.string,
  counter: PropTypes.number.isRequired,
  calculateDaysSinceSent: PropTypes.func.isRequired,
};

function SurveyFilters({
  surveys,
  surveyStatusFilter,
  surveyProjectFilter,
  surveyNameFilter,
  surveyAccountFilter,
  surveyAgeingFilter,
  onStatusChange,
  onProjectChange,
  onNameChange,
  onAccountChange,
  onAgeingChange,
  calculateDaysDiff,
  showStatus,
  showAgeing,
  showCsatFilter, // New prop
  onCsatChange,   // New prop
  surveyCsatFilter, // Add new prop
}) {
  return (
    <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
      {showStatus && (
        <div className="Project-form-control">
          {onStatusChange && (
            <select
              value={surveyStatusFilter}
              onChange={(e) => onStatusChange(e.target.value)}
              className="Project-filter-box-status Project-filter-select"
            >
              <option value="">Response Status</option>
              <option value="Responded">Responded</option>
              <option value="Not Responded">Not Responded</option>
              <option value="Expired">Expired</option>
            </select>
          )}
        </div>
      )}
      <select
        value={surveyProjectFilter}
        onChange={(e) => onProjectChange(e.target.value)}
        className="Project-form-control Project-filter-select Project-filter-box-project"
      >
        <option value="">Project</option>
        {[...new Set(surveys.map((s) => s.project).filter(Boolean))].map((project) => (
          <option key={`survey-project-${project}`} value={project}>
            {project}
          </option>
        ))}
      </select>
      <select
        value={surveyAccountFilter}
        onChange={(e) => onAccountChange(e.target.value)}
        className="Project-form-control Project-filter-select Project-filter-box-account"
      >
        <option value="">All Customers</option>
        {[...new Set(surveys.map((s) => `${s.account || 'N/A'}|${s.email || 'N/A'}`))]
          .filter(pair => pair !== 'N/A|N/A')
          .map((pair) => {
            const [account, email] = pair.split('|');
            return (
              <option key={`survey-account-email-${pair}`} value={pair}>
                {account} ({email})
              </option>
            );
          })}
      </select>
      <select
        value={surveyNameFilter}
        onChange={(e) => onNameChange(e.target.value)}
        className="Project-form-control Project-filter-select Project-filter-box-surveyname"
      >
        <option value="">Survey</option>
        {[...new Set(surveys.map((s) => s.surveyName).filter(Boolean))].map((name) => (
          <option key={`survey-name-${name}`} value={name}>
            {name}
          </option>
        ))}
      </select>
      {showAgeing && (
        <select
          value={surveyAgeingFilter}
          onChange={(e) => onAgeingChange(e.target.value)}
          className="Project-form-control Project-filter-select Project-filter-box-ageing"
        >
          <option value="">Days Ageing</option>
          <option value="lessThan15">Less than 15 days</option>
          <option value="15to30">15 to 30 days</option>
          <option value="greaterThan30">Greater than 30 days</option>
        </select>
      )}
      {/* New CSAT Breakdown Category Dropdown */}
      {showCsatFilter && (
        <select
          value={surveyCsatFilter}
          onChange={(e) => onCsatChange(e.target.value)}
          className="Project-form-control Project-filter-select Project-filter-box-csat"
        >
          <option value="">CSAT Breakdown Category</option>
          <option value="excellent">Excellent (4.5 - 5)</option>
          <option value="veryGood">Very Good (4 - 4.5)</option>
          <option value="good">Good (3 - 4)</option>
          <option value="dissatisfied">Dissatisfied (2 - 3)</option>
          <option value="veryDissatisfied">Very Dissatisfied (0 - 2)</option>
        </select>
      )}
    </Box>
  );
}

SurveyFilters.propTypes = {
  surveys: PropTypes.arrayOf(PropTypes.object).isRequired,
  surveyStatusFilter: PropTypes.string.isRequired,
  surveyProjectFilter: PropTypes.string.isRequired,
  surveyNameFilter: PropTypes.string.isRequired,
  surveyAccountFilter: PropTypes.string.isRequired,
  surveyAgeingFilter: PropTypes.string.isRequired,
  onStatusChange: PropTypes.func,
  onProjectChange: PropTypes.func.isRequired,
  onNameChange: PropTypes.func.isRequired,
  onAccountChange: PropTypes.func.isRequired,
  onAgeingChange: PropTypes.func.isRequired,
  calculateDaysDiff: PropTypes.func.isRequired,
  showStatus: PropTypes.bool,
  showAgeing: PropTypes.bool,
  showCsatFilter: PropTypes.bool,
  onCsatChange: PropTypes.func,
  surveyCsatFilter: PropTypes.string, // Add this
};

// Helper component for Surveys sent table
function SurveyTable({
  currentRows,
  surveyStatusFilter,
  selectedNotRespondedRows,
  sortConfig,
  onSort,
  onSelectRow,
  onSelectAll,
  allNotRespondedSelected,
  notRespondedCount,
}) {
  const calculateDaysDiff = (start, end = null) => {
    // Log inputs for debugging
    console.log('calculateDaysDiff inputs:', { start, end });

    if (!start || start === 'N/A') {
      console.warn('sentDate is missing or invalid:', start);
      return 'N/A';
    }

    const startDate = new Date(start);

    if (isNaN(startDate.getTime())) {
      console.warn('Invalid sentDate format:', start);
      return 'N/A';
    }

    // Handle 'N/A' or null end date by using current date
    let endDate;
    if (end === 'N/A' || end === null) {
      endDate = new Date(); // Use current date if no response or 'N/A'
    } else {
      endDate = new Date(end);
      if (isNaN(endDate.getTime())) {
        console.warn('Invalid RespondedDate format:', end);
        return 'N/A';
      }
    }

    const diffTime = Math.abs(endDate - startDate);
    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1; // ✅ inclusive

    // Log the calculated days for verification
    console.log('Calculated inclusive days:', days, { startDate, endDate });
    return days;
  };
  return (
    <div className="Project-project-table-container">
      <table className="Project-project-table">
        <thead>
          <tr>
            {surveyStatusFilter === 'Not Responded' && (
              <th className="Project-checkbox-header">
                <Checkbox
                  checked={allNotRespondedSelected}
                  onChange={onSelectAll}
                  disabled={notRespondedCount === 0}
                />
              </th>
            )}
            <th>
              Survey Name
              <button className="Project-sort-button" onClick={() => onSort('surveyName')}>
                <FontAwesomeIcon
                  icon={
                    sortConfig.key === 'surveyName'
                      ? sortConfig.direction === 'asc'
                        ? faSortUp
                        : faSortDown
                      : faSort
                  }
                />
              </button>
            </th>
            <th>Client</th>
            <th>
              Project
              <button className="Project-sort-button" onClick={() => onSort('project')}>
                <FontAwesomeIcon
                  icon={
                    sortConfig.key === 'project'
                      ? sortConfig.direction === 'asc'
                        ? faSortUp
                        : faSortDown
                      : faSort
                  }
                />
              </button>
            </th>
            <th>Customer Details</th>
            <th>
              Sent Date
              <button className="Project-sort-button" onClick={() => onSort('sentDate')}>
                <FontAwesomeIcon
                  icon={
                    sortConfig.key === 'sentDate'
                      ? sortConfig.direction === 'asc'
                        ? faSortUp
                        : faSortDown
                      : faSort
                  }
                />
              </button>
            </th>
            <th>Responded Date</th>
            <th>Days Ageing</th>
            <th>Mail Stats</th>
          </tr>
        </thead>
        <tbody>
          {currentRows && currentRows.length > 0 ? (
            currentRows.map((row) => (
              <tr
                key={`survey-row-${row.id}`}
                className={row.id % 2 === 0 ? 'Project-even-row' : 'Project-odd-row'}
              >
                {surveyStatusFilter === 'Not Responded' && (
                  <td>
                    {row.responseStatus === 'Not Responded' && (
                      <Checkbox
                        checked={selectedNotRespondedRows.includes(row.id)}
                        onChange={() => onSelectRow(row.id)}
                      />
                    )}
                  </td>
                )}
                <td data-tooltip={row.surveyName}>{row.surveyName || 'N/A'}</td>
                <td data-tooltip={row.client}>{row.client || 'N/A'}</td>
                <td data-tooltip={row.project}>{row.project || 'N/A'}</td>
                <td>
                  <div>{row.account || 'N/A'}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {row.email || 'N/A'}
                  </div>
                </td>
                <td data-tooltip={row.sentDate}>{row.sentDate || 'N/A'}</td>
                <td data-tooltip={row.responseStatus === 'Responded' ? row.RespondedDate : row.responseStatus}>
                  {row.responseStatus === 'Responded' ? (row.RespondedDate || 'N/A') : row.responseStatus}
                </td>
                <td data-tooltip={calculateDaysDiff(row.sentDate, row.RespondedDate)}>
                  {calculateDaysDiff(row.sentDate, row.RespondedDate)}
                </td>
                <td data-tooltip={row.counter != null ? row.counter.toString() : 'N/A'}>
                  {row.counter || 'N/A'}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={surveyStatusFilter === 'Not Responded' ? 9 : 8}
                style={{ textAlign: 'center', padding: '20px', color: '#666' }}
              >
                No data found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// PropTypes
SurveyTable.propTypes = {
  currentRows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      surveyName: PropTypes.string,
      client: PropTypes.string,
      project: PropTypes.string,
      account: PropTypes.string,
      email: PropTypes.string,
      sentDate: PropTypes.string,
      RespondedDate: PropTypes.string,
      responseStatus: PropTypes.string,
      counter: PropTypes.number,
    })
  ).isRequired,
  surveyStatusFilter: PropTypes.string.isRequired,
  selectedNotRespondedRows: PropTypes.arrayOf(PropTypes.number).isRequired,
  sortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.string,
  }).isRequired,
  onSort: PropTypes.func.isRequired,
  onSelectRow: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  allNotRespondedSelected: PropTypes.bool.isRequired,
  notRespondedCount: PropTypes.number.isRequired,
};

function calculateFeedbackBreakdown(csatPercentages) {
  const { excellent, veryGood, good, dissatisfied, veryDissatisfied } = csatPercentages;
  const excellentNum = parseFloat(excellent) || 0;
  const veryGoodNum = parseFloat(veryGood) || 0;
  const goodNum = parseFloat(good) || 0;
  const dissatisfiedNum = parseFloat(dissatisfied) || 0;
  const veryDissatisfiedNum = parseFloat(veryDissatisfied) || 0;

  if (excellentNum === 0 && veryGoodNum === 0 && goodNum === 0 && dissatisfiedNum === 0 && veryDissatisfiedNum === 0) {
    return ['No valid feedback data available'];
  }

  const maxPercentage = Math.max(excellentNum, veryGoodNum, goodNum, dissatisfiedNum, veryDissatisfiedNum);
  if (maxPercentage === veryDissatisfiedNum && veryDissatisfiedNum > 0) {
    return ['Very Dissatisfied response - Poor overall'];
  }
  if (maxPercentage === dissatisfiedNum && dissatisfiedNum > 0) {
    return ['Dissatisfied response - Below average overall'];
  }
  if (maxPercentage === goodNum && goodNum > 0) {
    return ['Good response - Average overall'];
  }
  if (maxPercentage === veryGoodNum && veryGoodNum > 0) {
    return ['Very Good response - Above average overall'];
  }
  if (maxPercentage === excellentNum && excellentNum > 0) {
    return ['Excellent response - Outstanding overall'];
  }

  return ['No feedback available'];
}

// Helper component for Completed Surveys table with Bar Chart
function CompletedSurveyTable({ currentRows, sortConfig, onSort, csatPercentages, onSurveyClick }) {
  const barChartData = {
    labels: ['Excellent', 'Very Good', 'Good', 'Dissatisfied', 'Very Dissatisfied'],
    datasets: [
      {
        label: 'CSAT Distribution',
        data: [
          parseFloat(csatPercentages.excellent) || 0,
          parseFloat(csatPercentages.veryGood) || 0,
          parseFloat(csatPercentages.good) || 0,
          parseFloat(csatPercentages.dissatisfied) || 0,
          parseFloat(csatPercentages.veryDissatisfied) || 0,
        ],
        backgroundColor: ['#00BCD4', '#4CAF50', '#FF9800', '#F44336', '#B71C1C'],
        borderWidth: 0,
        barThickness: 16, // Adjusted for five bars to fit better
      },
    ],
  };

  const barChartOptions = {
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => {
            const value = context.raw || 0;
            return `${context.label}: ${value}%`;
          },
        },
      },
      datalabels: {
        display: (context) => context.raw > 0,
        anchor: 'center',
        align: 'center',
        formatter: (value) => `${value}%`,
        color: '#333',
        font: {
          family: "'Mulish', sans-serif",
          size: 14,
          weight: '600',
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        ticks: { display: false },
        grid: { display: false },
      },
      y: {
        ticks: {
          font: { family: "'Mulish', sans-serif", size: 14, weight: '600' },
          color: '#333',
        },
        grid: { display: false },
      },
    },
    maintainAspectRatio: false,
  };
  // Calculate feedback breakdown
  const feedbackBreakdown = calculateFeedbackBreakdown(csatPercentages);

  return (
    <div className="Project-project-table-container">
      <div className="Project-chart-feedback-container">
        <div className="Project-feedback-breakdown">
          <Typography variant="h6" className="Project-feedback-title">
            Feedback Breakdown
          </Typography>
          {feedbackBreakdown.length > 0 ? (
            feedbackBreakdown.map((feedback, index) => (
              <Typography key={`feedback-${index}`} className="Project-feedback-item">
                {feedback}
              </Typography>
            ))
          ) : (
            <Typography className="Project-feedback-item">No feedback available.</Typography>
          )}
        </div>
        <div className="Project-bar-chart-container">
          <Bar data={barChartData} options={barChartOptions} height={80} />
        </div>
      </div>
      <table className="Project-project-table">
        <thead>
          <tr>
            <th>
              Survey Name
              <button className="Project-sort-button" onClick={() => onSort('surveyName')}>
                <FontAwesomeIcon
                  icon={sortConfig.key === 'surveyName' ? (sortConfig.direction === 'asc' ? faSortUp : faSortDown) : faSort}
                />
              </button>
            </th>
            <th>
              Client
              <button className="Project-sort-button" onClick={() => onSort('client')}>
                <FontAwesomeIcon
                  icon={sortConfig.key === 'client' ? (sortConfig.direction === 'asc' ? faSortUp : faSortDown) : faSort}
                />
              </button>
            </th>
            <th>
              Project
              <button className="Project-sort-button" onClick={() => onSort('project')}>
                <FontAwesomeIcon
                  icon={sortConfig.key === 'project' ? (sortConfig.direction === 'asc' ? faSortUp : faSortDown) : faSort}
                />
              </button>
            </th>
            <th>Customer Details</th>
            <th>
              Responded Date
              <button className="Project-sort-button" onClick={() => onSort('date')}>
                <FontAwesomeIcon
                  icon={sortConfig.key === 'date' ? (sortConfig.direction === 'asc' ? faSortUp : faSortDown) : faSort}
                />
              </button>
            </th>
            <th>CSAT</th>
          </tr>
        </thead>

        <tbody>
          {currentRows && currentRows.length > 0 ? (
            currentRows.map((row) => (
              <tr key={`completed-row-${row.id}`} className={row.id % 2 === 0 ? 'Project-even-row' : 'Project-odd-row'}>
                {/* Survey Name */}
                <td title={row.surveyName || 'N/A'}>
                  <button
                    onClick={() => onSurveyClick(row.surveyId, row.clientId)}
                    style={{
                      color: '#03519f',
                      textDecoration: 'underline',
                      background: 'none',
                      border: 'none',
                      cursor: row.surveyId && row.clientId ? 'pointer' : 'default',
                      opacity: row.surveyId && row.clientId ? 1 : 0.5,
                      textAlign: 'left',
                    }}
                    disabled={!row.surveyId || !row.clientId}
                  >
                    {row.surveyName || 'N/A'}
                  </button>
                </td>

                {/* Client */}
                <td>{row.client || 'N/A'}</td>

                {/* Project */}
                <td>{row.project || 'N/A'}</td>

                {/* Customer Details (Name + Email on separate lines) */}
                <td>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span>{row.account || "N/A"}</span>
                    <span style={{ color: "#555", fontSize: "0.9em" }}>
                      {row.email || ""}
                    </span>
                  </div>
                </td>

                {/* Date */}
                <td>{row.date || 'N/A'}</td>

                {/* CSAT */}
                <td>{row.csat || 'N/A'}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                No data found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

CompletedSurveyTable.propTypes = {
  currentRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  sortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.string,
  }).isRequired,
  onSort: PropTypes.func.isRequired,
  csatPercentages: PropTypes.shape({
    excellent: PropTypes.number,
    veryGood: PropTypes.number,
    good: PropTypes.number,
    dissatisfied: PropTypes.number,
    veryDissatisfied: PropTypes.number,
  }).isRequired,
  onSurveyClick: PropTypes.func.isRequired,
};

function filterSurveys(surveys, surveyStatusFilter, surveyProjectFilter, surveyNameFilter, surveyAccountFilter, calculateDaysSinceSent, surveyAgeingFilter) {
  return Array.isArray(surveys)
    ? surveys
      .map((survey) => {
        let responseStatus;
        if (survey.RespondedDate !== 'N/A' && survey.RespondedDate !== null) {
          responseStatus = 'Responded';
        } else if (survey.counter >= 3) {
          responseStatus = 'Expired';
        } else {
          responseStatus = 'Not Responded';
        }
        return {
          ...survey,
          responseStatus,
        };
      })
      .filter((row) => {
        const matchesStatus = !surveyStatusFilter || row.responseStatus === surveyStatusFilter;
        const matchesProject = !surveyProjectFilter || row.project === surveyProjectFilter;
        const matchesSurveyName = !surveyNameFilter || (row.surveyName && row.surveyName.toLowerCase().includes(surveyNameFilter.toLowerCase()));
        const matchesAccount = !surveyAccountFilter || `${row.account || 'N/A'}|${row.email || 'N/A'}` === surveyAccountFilter;
        const days = calculateDaysDiff(row.sentDate, row.RespondedDate);
        const matchesAgeing = surveyAgeingFilter
          ? days !== 'N/A' &&
          ((surveyAgeingFilter === 'lessThan15' && days < 15) ||
            (surveyAgeingFilter === '15to30' && days >= 15 && days <= 30) ||
            (surveyAgeingFilter === 'greaterThan30' && days > 30))
          : true;
        return matchesStatus && matchesProject && matchesSurveyName && matchesAccount && matchesAgeing;
      })
    : [];
}

function filterCompletedSurveys(completedSurveys, surveyProjectFilter, surveyNameFilter, surveyAccountFilter, csatFilter) {
  return Array.isArray(completedSurveys)
    ? completedSurveys.filter((row) => {
      const matchesProject = !surveyProjectFilter || row.project === surveyProjectFilter;
      const matchesSurveyName = !surveyNameFilter || (row.surveyName && row.surveyName.toLowerCase().includes(surveyNameFilter.toLowerCase()));
      const matchesAccount = !surveyAccountFilter || `${row.account || 'N/A'}|${row.email || 'N/A'}` === surveyAccountFilter;

      // Add CSAT category filtering
      const csat = parseFloat(row.csat);
      let matchesCsat = true;
      if (csatFilter && !isNaN(csat)) {
        switch (csatFilter) {
          case 'excellent':
            matchesCsat = csat >= 4.5 && csat <= 5;
            break;
          case 'veryGood':
            matchesCsat = csat >= 4 && csat < 4.5;
            break;
          case 'good':
            matchesCsat = csat >= 3 && csat < 4;
            break;
          case 'dissatisfied':
            matchesCsat = csat >= 2 && csat < 3;
            break;
          case 'veryDissatisfied':
            matchesCsat = csat >= 0 && csat < 2; // Assuming CSAT minimum is 0 or 1, adjust if needed
            break;
          default:
            matchesCsat = true;
        }
      }

      return matchesProject && matchesSurveyName && matchesAccount && matchesCsat;
    })
    : [];
}

const Projects = () => {
  const [projects, setProjects] = useState(() => {
    const cachedProjects = localStorage.getItem('cachedProjects');
    return cachedProjects ? JSON.parse(cachedProjects) : [];
  });
  const [surveys, setSurveys] = useState([]);
  const [completedSurveys, setCompletedSurveys] = useState([]);
  const [csatData, setCsatData] = useState([]);
  const [atRiskData, setAtRiskData] = useState([]);
  const [riskFactorFilter, setRiskFactorFilter] = useState('');
  const [apiSurveys, setApiSurveys] = useState([]);
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [resendSurveys, setResendSurveys] = useState([]);
  const [statistics, setStatistics] = useState(() => {
    const cachedStatistics = localStorage.getItem('cachedStatistics');
    return cachedStatistics ? JSON.parse(cachedStatistics) : null;
  });
  const [statuses, setStatuses] = useState(() => {
    const cachedStatuses = localStorage.getItem('cachedStatuses');
    let statuses = cachedStatuses ? JSON.parse(cachedStatuses) : [];
    if (Array.isArray(statuses) && statuses.length > 0 && typeof statuses[0] === 'string') {
      statuses = statuses.map((name, index) => ({ ID: index + 1, Name: name }));
      localStorage.setItem('cachedStatuses', JSON.stringify(statuses));
    } else if (!Array.isArray(statuses)) {
      statuses = [];
    }
    return statuses;
  });
  const [types, setTypes] = useState(() => {
    const cachedTypes = localStorage.getItem('cachedTypes');
    let types = cachedTypes ? JSON.parse(cachedTypes) : [];
    if (Array.isArray(types) && types.length > 0 && typeof types[0] === 'string') {
      types = types.map((name, index) => ({ ID: index + 1, Name: name }));
      localStorage.setItem('cachedTypes', JSON.stringify(types));
    } else if (!Array.isArray(types)) {
      types = [];
    }
    return types;
  });
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [popupDataLoading, setPopupDataLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [surveyResponseFilter, setSurveyResponseFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [projectTypeFilter, setProjectTypeFilter] = useState('All');
  const [distinctTypes, setDistinctTypes] = useState([]);
  const [practiceFilter, setPracticeFilter] = useState('');
  const [distinctPractices, setDistinctPractices] = useState([]);
  const [endingInFilter, setEndingInFilter] = useState(''); // Add endingInFilter state
  const [activePopup, setActivePopup] = useState(null);
  const [slideDirection, setSlideDirection] = useState('');
  const [surveyStatusFilter, setSurveyStatusFilter] = useState('');
  const [surveyAgeingFilter, setSurveyAgeingFilter] = useState('');
  const [surveyProjectFilter, setSurveyProjectFilter] = useState('');
  const [surveyNameFilter, setSurveyNameFilter] = useState('');
  const [surveyAccountFilter, setSurveyAccountFilter] = useState('');
  const [csatFilter, setCsatFilter] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPageSurveys, setRowsPerPageSurveys] = useState(10);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  // const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [sortConfig, setSortConfig] = useState(() => {
    return activePopup === 'csat'
      ? { key: 'csat', direction: 'desc' }
      : { key: null, direction: 'asc' };
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedNotRespondedRows, setSelectedNotRespondedRows] = useState([]);
  const [transitioning, setTransitioning] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [showRuddrContactsDialog, setShowRuddrContactsDialog] = useState(false);
  const [selectedProjectForContacts, setSelectedProjectForContacts] = useState(null);
  const [practices, setPractices] = useState(() => {
    const cachedPractices = localStorage.getItem('cachedPractices');
    let practices = cachedPractices ? JSON.parse(cachedPractices) : [];
    if (!Array.isArray(practices)) {
      practices = [];
    }
    return practices;
  });

  const moduleAccess = JSON.parse(sessionStorage.getItem('module_access') || '{}');
  const hasWriteAccess = moduleAccess.Projects === 'Read/Write';
  const accessToken = Cookies.get('access_token');

  const fetchData = useCallback(async () => {
    setTableLoading(true);
    if (
      !localStorage.getItem('cachedProjects') ||
      !localStorage.getItem('cachedStatuses') ||
      !localStorage.getItem('cachedTypes')
    ) {
      setLoading(true);
    }

    try {
      const userId = sessionStorage.getItem('user_id');
      const PracticeId = sessionStorage.getItem('practice_id');
      if (!userId) {
        throw new Error('User ID is not available. Please log in again.');
      }

      // Determine project type for API based on dropdown selection
      let projectTypeParam = 'all';
      if (projectTypeFilter === 'Survey Projects') {
        projectTypeParam = 'survey';
      } else if (projectTypeFilter === 'Ruddr Projects') {
        projectTypeParam = 'ruddr';
      }

      const projectPromise = apiService.fetchData(`projects/${userId}/${PracticeId}/${projectTypeParam}`);

      const statsPromise = apiService.fetchData('projects/statistics');

      const practicesPromise = apiService.fetchData('teams');

      Promise.any([projectPromise, statsPromise, practicesPromise])
        .then((firstResult) => {
          if (firstResult === projectPromise) {
            // Normalize projects response shape:
            // supports { data: { results: [] } }, { results: [] }, { data: [] }, or a plain array
            let projectList = [];
            const projectContainer = firstResult?.data || firstResult;

            if (Array.isArray(projectContainer?.results)) {
              projectList = projectContainer.results;
            } else if (Array.isArray(projectContainer)) {
              projectList = projectContainer;
            }

            setProjects(projectList);
            localStorage.setItem('cachedProjects', JSON.stringify(projectList));
            setTableLoading(false);
          } else if (firstResult === statsPromise) {
            if (firstResult.status === 'success' && firstResult.results && firstResult.results.length > 0) {
              const newStatistics = firstResult.results[0];
              const cachedStatistics = JSON.parse(localStorage.getItem('cachedStatistics') || '{}');
              if (!areObjectsEqual(newStatistics, cachedStatistics)) {
                setStatistics(newStatistics);
                localStorage.setItem('cachedStatistics', JSON.stringify(newStatistics));
              }
            }
          } else if (firstResult === practicesPromise) {
            const practiceList = Array.isArray(firstResult.results) ? firstResult.results : [];
            setPractices(practiceList);
            localStorage.setItem('cachedPractices', JSON.stringify(practiceList));
          }
          setLoading(false);
        })
        .catch(() => {
          setSnackbarMessage('Failed to load critical data. Showing cached data if available.');
          setSnackbarOpen(true);
          setTableLoading(false);
        });

      const results = await Promise.allSettled([projectPromise, statsPromise, practicesPromise]);

      if (results[0].status === 'fulfilled') {
        const projectResult = results[0].value;

        // Normalize projects response shape here as well
        let projectList = [];
        const projectContainer = projectResult?.data || projectResult;

        if (Array.isArray(projectContainer?.results)) {
          projectList = projectContainer.results;
        } else if (Array.isArray(projectContainer)) {
          projectList = projectContainer;
        }

        setProjects(projectList);
        localStorage.setItem('cachedProjects', JSON.stringify(projectList));
        setTableLoading(false);
      } else {
        console.error('Project API failed:', results[0].reason);
        setSnackbarMessage('Failed to load projects. Using cached data.');
        setSnackbarOpen(true);
        setTableLoading(false);
      }

      if (results[1].status === 'fulfilled') {
        if (results[1].value.status === 'success' && results[1].value.results && results[1].value.results.length > 0) {
          const newStatistics = results[1].value.results[0];
          const cachedStatistics = JSON.parse(localStorage.getItem('cachedStatistics') || '{}');
          if (!areObjectsEqual(newStatistics, cachedStatistics)) {
            setStatistics(newStatistics);
            localStorage.setItem('cachedStatistics', JSON.stringify(newStatistics));
          }
        }
      } else {
        console.error('Statistics API failed:', results[1].reason);
        setSnackbarMessage('Failed to load statistics. Using cached data.');
        setSnackbarOpen(true);
      }

      if (results[2].status === 'fulfilled') {
        const practiceList = Array.isArray(results[2].value.results) ? results[2].value.results : [];
        setPractices(practiceList);
        localStorage.setItem('cachedPractices', JSON.stringify(practiceList));
      } else {
        console.error('Practices API failed:', results[2].reason);
        setSnackbarMessage('Failed to load practices. Using cached data.');
        setSnackbarOpen(true);
      }

      const fetchNonCriticalData = async () => {
        try {
          setPopupDataLoading(true);
          const statusData = await apiService.fetchData('projects/status');
          let statusList = Array.isArray(statusData.results) ? statusData.results : [];
          if (statusList.length > 0 && typeof statusList[0] === 'string') {
            statusList = statusList.map((name, index) => ({ ID: index + 1, Name: name }));
          } else if (!Array.isArray(statusList)) {
            statusList = [];
          }
          setStatuses(statusList);
          localStorage.setItem('cachedStatuses', JSON.stringify(statusList));

          const typeData = await apiService.fetchData('projects/type');
          let typeList = Array.isArray(typeData.results) ? typeData.results : [];
          if (typeList.length > 0 && typeof typeList[0] === 'string') {
            typeList = typeList.map((name, index) => ({ ID: index + 1, Name: name }));
          } else if (!Array.isArray(typeList)) {
            typeList = [];
          }
          setTypes(typeList);
          localStorage.setItem('cachedTypes', JSON.stringify(typeList));

          const surveyData = await apiService.fetchData('surveys');
          setApiSurveys(surveyData.results || []);

          const surveySentData = await apiService.fetchData('projects/surveysent');
          const formattedSurveys = Array.isArray(surveySentData.results)
            ? surveySentData.results.map((survey, index) => {
              const startDate = survey.SentOn ? new Date(survey.SentOn) : null;
              const formattedDate =
                startDate && !isNaN(startDate.getTime())
                  ? `${(startDate.getMonth() + 1).toString().padStart(2, '0')}/${startDate
                    .getDate()
                    .toString()
                    .padStart(2, '0')}/${startDate.getFullYear().toString().slice(2)}`
                  : 'N/A';

              // ✅ Responded Date
              const endDate = survey.RespStartTime ? new Date(survey.RespStartTime) : null;
              const formattedEndDate =
                endDate && !isNaN(endDate.getTime())
                  ? `${(endDate.getMonth() + 1).toString().padStart(2, '0')}/${endDate
                    .getDate()
                    .toString()
                    .padStart(2, '0')}/${endDate.getFullYear().toString().slice(2)}`
                  : 'N/A';
              return {
                id: index + 1,
                surveyName: survey.SurveyName || 'N/A',
                project: survey.ProjectName || 'N/A',
                client: survey.ClientName || 'N/A',
                account: survey.Account || 'N/A',
                sentDate: formattedDate,
                RespondedDate: formattedEndDate,
                responseStatus: survey.ResponseStatus || 'Not Responded',
                counter: survey.EmailCounter || 1,
                email: survey.Email || 'N/A',
                contactId: survey.ContactId || null,
                surveyId: survey.SurveyId || null,
                projectId: survey.ProjectID || null,
                UniqueID: survey.UniqueID || ''
              };
            })
            : [];
          setSurveys(formattedSurveys);

          const surveyCompletedData = await apiService.fetchData('projects/surveycompleted');
          const formattedCompletedSurveys = Array.isArray(surveyCompletedData.results)
            ? surveyCompletedData.results.map((survey, index) => {
              let formattedDate = 'N/A';
              if (survey.RespStartTime) {
                const date = new Date(survey.RespStartTime);
                if (!isNaN(date.getTime())) {
                  formattedDate = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear().toString().slice(2)}`;
                }
              }
              return {
                id: survey.ClientID || index + 1,
                surveyName: survey['Survey Name'] || 'N/A',
                project: survey.Project || 'N/A',
                client: survey.Client || 'N/A',
                account: survey.Customers || 'N/A',
                email: survey.CustomerEmail || 'N/A',
                date: formattedDate,
                csat: survey.CSAT || 'N/A',
                surveyId: survey.SurveyID || null,
                clientId: survey.ClientID || null,
              };
            })
            : [];
          setCompletedSurveys(formattedCompletedSurveys);

          const atRiskDataResponse = await apiService.fetchData('projects/atrisk');
          const formattedAtRiskData = Array.isArray(atRiskDataResponse.results)
            ? atRiskDataResponse.results.map((item, index) => {
              let formattedDate = 'N/A';
              if (item.ActivityDate) {
                try {
                  const date = new Date(item.ActivityDate);
                  if (!isNaN(date.getTime())) {
                    formattedDate = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear().toString().slice(2)}`;
                  }
                } catch (error) {
                  console.warn('Error parsing ActivityDate:', item.ActivityDate, error);
                }
              }
              return {
                id: item.RecordID || index + 1,
                surveyName: `Survey ${item.SurveyId}` || 'N/A',
                project: item.ProjectName || 'N/A',
                account: item.ClientName || 'N/A',
                date: formattedDate,
                riskFactor: item.ResponseStatus === 'Responded' ? 'Poor CSAT Score' : 'Low Survey Response',
                surveyId: item.SurveyId || null,
                clientId: item.ClientId || null,
              };
            })
            : [];
          setAtRiskData(formattedAtRiskData);

          const csatScoreData = await apiService.fetchData('projects/csatscore');
          const formattedCsatData = Array.isArray(csatScoreData.results)
            ? csatScoreData.results.map((csat, index) => {
              let formattedDate = 'N/A';
              if (csat.StartDate) {
                const date = new Date(csat.StartDate);
                if (!isNaN(date.getTime())) {
                  formattedDate = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear().toString().slice(2)}`;
                }
              }
              return {
                id: csat.ID || index + 1,
                client: csat.ClientName,
                project: csat.ProjectName || 'N/A',
                account: csat.ACCOUNT || 'N/A',
                date: formattedDate,
                csat: csat.AvgCSATScore != null ? csat.AvgCSATScore.toString() : 'N/A', // Ensure csat is a string
              };
            })
            : [];
          setCsatData(formattedCsatData);

          const practiceData = await apiService.fetchData('teams');
          const practiceList = Array.isArray(practiceData.results) ? practiceData.results : [];
          setPractices(practiceList);
          localStorage.setItem('cachedPractices', JSON.stringify(practiceList));
          setPopupDataLoading(false);
        } catch (error) {
          console.error('fetchNonCriticalData error:', error);
          setSnackbarMessage('Failed to load additional data. Some features may be limited.');
          setSnackbarOpen(true);
          setPopupDataLoading(false);
        }
      };

      await fetchNonCriticalData();
    } catch (error) {
      console.error('fetchData error:', error);
      setSnackbarMessage('Failed to load critical data. Showing cached data if available.');
      setSnackbarOpen(true);
      setLoading(false);
      setTableLoading(false);
    }
  }, [accessToken, projectTypeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData, projectTypeFilter]);

  const handleSurveyClick = (surveyId, clientId) => {
    setSelectedSurveyId(surveyId);
    setSelectedClientId(clientId);
  };

  const handleClosePopup = () => {
    setSelectedSurveyId(null);
    setSelectedClientId(null);
  };


  const projectStats = statistics
    ? [
      { label: 'Surveys sent', value: Number(statistics.SurveySent) || 0, color: 'Project-service-sent' },
      { label: 'Surveys Completed', value: Number(statistics.CompletedSurvey) || 0, color: 'Project-survey-completed' },
    ]
    : [
      { label: 'Surveys sent', value: 0, color: 'Project-service-sent' },
      { label: 'Surveys Completed', value: 0, color: 'Project-survey-completed' },
    ];

  function calculateDaysSinceSent(sentDate, counter, responseStatus) {
    if (responseStatus === 'Responded') {
      return 'Survey Completed';
    }

    if (!sentDate || typeof sentDate !== 'string') {
      return 'N/A';
    }

    try {
      const [month, day, year] = sentDate.split('/').map(Number);
      if (!month || !day || !year) {
        return 'N/A';
      }
      const fullYear = 2000 + year;
      const sent = new Date(fullYear, month - 1, day);
      if (isNaN(sent.getTime())) {
        return 'N/A';
      }
      const today = new Date();
      const diffTime = Math.abs(today - sent);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Check if survey is expired (counter >= 3)
      if (counter >= 3) {
        return 'Survey Expired';
      }

      // Return days if not expired
      return `${diffDays} days`;
    } catch (error) {
      console.warn('calculateDaysSinceSent error:', error);
      return 'N/A';
    }
  }
  const filteredSurveys = filterSurveys(surveys, surveyStatusFilter, surveyProjectFilter, surveyNameFilter, surveyAccountFilter, calculateDaysSinceSent, surveyAgeingFilter);
  const filteredCompletedSurveys = filterCompletedSurveys(completedSurveys, surveyProjectFilter, surveyNameFilter, surveyAccountFilter, csatFilter);

  // Reset page when filters change to prevent invalid page numbers
  useEffect(() => {
    setPage(1);
  }, [surveyStatusFilter, surveyProjectFilter, surveyNameFilter, surveyAccountFilter, csatFilter, surveyAgeingFilter]);

  const csatDistribution = useMemo(() => {
    return filteredCompletedSurveys.reduce(
      (acc, survey) => {
        const csat = parseFloat(survey.csat);
        if (!isNaN(csat)) {
          if (csat >= 4.5 && csat <= 5) acc.excellent += 1;
          else if (csat >= 4 && csat < 4.5) acc.veryGood += 1;
          else if (csat >= 3 && csat < 4) acc.good += 1;
          else if (csat >= 2 && csat < 3) acc.dissatisfied += 1;
          else if (csat >= 1 && csat < 2) acc.veryDissatisfied += 1;
        }
        return acc;
      },
      { excellent: 0, veryGood: 0, good: 0, dissatisfied: 0, veryDissatisfied: 0 }
    );
  }, [filteredCompletedSurveys]);

  const totalSurveys = csatDistribution.excellent + csatDistribution.veryGood + csatDistribution.good + csatDistribution.dissatisfied + csatDistribution.veryDissatisfied;
  const csatPercentages = useMemo(() => ({
    excellent: totalSurveys > 0 ? ((csatDistribution.excellent / totalSurveys) * 100).toFixed(1) : 0,
    veryGood: totalSurveys > 0 ? ((csatDistribution.veryGood / totalSurveys) * 100).toFixed(1) : 0,
    good: totalSurveys > 0 ? ((csatDistribution.good / totalSurveys) * 100).toFixed(1) : 0,
    dissatisfied: totalSurveys > 0 ? ((csatDistribution.dissatisfied / totalSurveys) * 100).toFixed(1) : 0,
    veryDissatisfied: totalSurveys > 0 ? ((csatDistribution.veryDissatisfied / totalSurveys) * 100).toFixed(1) : 0,
  }), [csatDistribution]);

  const pieChartData = useMemo(() => ({
    labels: ['Responded', 'Not Responded', 'Expired'],
    datasets: [
      {
        data: [
          filteredSurveys.filter((s) => s.responseStatus === 'Responded').length,
          filteredSurveys.filter((s) => s.responseStatus === 'Not Responded').length,
          filteredSurveys.filter((s) => s.responseStatus === 'Expired').length,
        ],
        backgroundColor: ['#4CAF50', '#F44336', '#FF9800'], // Added orange for Expired
        borderColor: ['#388E3C', '#D32F2F', '#F57C00'], // Corresponding border colors
        borderWidth: 1,
      },
    ],
  }), [filteredSurveys]);
  // Calculate ageing distribution for bar chart
  // Calculate ageing distribution for bar chart
  const ageingDistribution = useMemo(() => calculateAgeingDistribution(filteredSurveys), [filteredSurveys]);

  const barChartData = useMemo(() => ({
    labels: ['Less than 15 days', '15 to 30 days', 'Greater than 30 days'],
    datasets: [
      {
        label: 'Days Ageing Distribution',
        data: [
          ageingDistribution.lessThan15,
          ageingDistribution.between15and30,
          ageingDistribution.greaterThan30,
        ],
        backgroundColor: ['#00BCD4', '#FF9800', '#F44336'],
        borderWidth: 0,
        barThickness: 30,
      },
    ],
  }), [ageingDistribution]);

  const barChartOptions = {
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => {
            const value = context.raw || 0;
            return `${context.label}: ${value} surveys`;
          },
        },
      },
      datalabels: {
        display: (context) => context.raw > 0,
        anchor: 'center',
        align: 'center',
        formatter: (value) => `${value}`,
        color: '#333',
        font: {
          family: "'Mulish', sans-serif",
          size: 14,
          weight: '600',
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          font: { family: "'Mulish', sans-serif", size: 12 },
          color: '#333',
          stepSize: 1,
        },
        grid: { display: false },
      },
      y: {
        ticks: {
          font: { family: "'Mulish', sans-serif", size: 14, weight: '600' },
          color: '#333',
        },
        grid: { display: false },
      },
    },
    maintainAspectRatio: false,
  };

  const pieChartOptions = {
    plugins: {
      legend: {
        position: 'right',
        labels: {
          font: { family: "'Mulish', sans-serif", size: 14 },
          color: '#333',
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
      datalabels: {
        display: true,
        color: '#ffffff',
        font: {
          family: "'Mulish', sans-serif",
          size: 14,
          weight: '600',
        },
        formatter: (value, context) => {
          const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          return `${percentage}%`;
        },
      },
    },
    maintainAspectRatio: false,
  };

  const handleAddProject = async (projectData) => {
    setOpenDialog(false);
    try {
      await apiService.postData('projects', projectData, accessToken);
      const userId = sessionStorage.getItem('user_id');
      const PracticeId = sessionStorage.getItem('practice_id');
      let projectTypeParam = 'all';
      if (projectTypeFilter === 'Survey Projects') {
        projectTypeParam = 'survey';
      } else if (projectTypeFilter === 'Ruddr Projects') {
        projectTypeParam = 'ruddr';
      }
      const projectDataResponse = await apiService.fetchData(`projects/${userId}/${PracticeId}/${projectTypeParam}`);
      const projectContainer = projectDataResponse?.data || projectDataResponse;
      let projectList = [];
      if (Array.isArray(projectContainer?.results)) {
        projectList = projectContainer.results;
      } else if (Array.isArray(projectContainer)) {
        projectList = projectContainer;
      }
      setProjects(projectList);
      localStorage.setItem('cachedProjects', JSON.stringify(projectList));
      setSnackbarMessage('Project added successfully.');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(`Failed to add project: ${error.response?.data?.message || error.message}`);
      setSnackbarOpen(true);
    }
  };

  const statusMap = useMemo(() => statuses.reduce((map, status) => {
    const name = typeof status === 'string' ? status : status.Name;
    const id = typeof status === 'string' ? statuses.indexOf(status) + 1 : status.ID;
    map[id.toString()] = name;
    map[name.toLowerCase()] = name;
    return map;
  }, {}), [statuses]);

  const typeMap = useMemo(() => types.reduce((map, type) => {
    const name = typeof type === 'string' ? type : type.Name;
    const id = typeof type === 'string' ? types.indexOf(type) + 1 : type.ID;
    map[id.toString()] = name;
    map[name.toLowerCase()] = name;
    return map;
  }, {}), [types]);

  const filteredProjects = useMemo(() => {
    const mapped = projects.map((project) => {
      const statusKey = project.Status ? project.Status.toString().toLowerCase() : '';
      const displayStatus = statusMap[statusKey] || statusMap[project.Status] || project.Status || 'N/A';
      const typeKey = project.Type ? project.Type.toString().toLowerCase() : '';
      const displayType = typeMap[typeKey] || typeMap[project.Type] || project.Type || 'N/A';

      let formattedStartDate = 'N/A';
      if (project.GoLiveDate) {
        try {
          const date = new Date(project.GoLiveDate);
          if (!isNaN(date.getTime())) {
            formattedStartDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
          }
        } catch (error) {
          console.warn('Error parsing GoLiveDate:', project.GoLiveDate, error);
        }
      }

      let formattedEndDate = 'N/A';
      if (project.EndDate) {
        try {
          const date = new Date(project.EndDate);
          if (!isNaN(date.getTime())) {
            formattedEndDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
          }
        } catch (error) {
          console.warn('Error parsing EndDate:', project.EndDate, error);
        }
      }

      return {
        id: project.ID,
        'Project Name': project.Name || 'N/A',
        Practice: project.PracticeName || project.Practice || 'N/A',
        'Start Date': formattedStartDate,
        'End Date': formattedEndDate,
        Status: displayStatus,
        Type: displayType,
        Client: project.Client,
        Description: project.Description || 'N/A',
        'Created By': project.CreatedBy || 'N/A',
        'Response Received?': project.SurveyResponse,
        'Survey Sent?': project.SurveySent,
        'Response Date': project.ResponseDate
          ? new Date(project.ResponseDate).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
          : 'N/A',
        CSAT: project.SurveyResponses || 'N/A',
        FullName: project.FullName || '',
        Email: project.Email || '',
        CustomerContactIDs: project.CustomerContactIDs || '',
        rawEndDate: project.EndDate,
        ProjectType: project.ProjectType || 'survey'
      };
    });
    return mapped.filter((row) => {
      const matchesSearch = Object.values(row).some((value) =>
        value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchesStatus = !statusFilter || row.Status === statusFilter;
      const matchesSurveyResponse =
        !surveyResponseFilter ||
        surveyResponseFilter === '' ||
        (surveyResponseFilter === 'Yes' && row['Response Received?'] === 'Yes') ||
        (surveyResponseFilter === 'No' && row['Response Received?'] === 'No');
      const matchesType =
        !typeFilter ||
        typeFilter === '' ||
        row.Type === typeFilter;
      const matchesPractice =
        !practiceFilter ||
        practiceFilter === '' ||
        row.Practice === practiceFilter;
      const matchesEndingIn = !endingInFilter || (() => {
        if (!row.rawEndDate || row.rawEndDate === 'N/A') return false;
        try {
          const endDate = new Date(row.rawEndDate);
          if (isNaN(endDate.getTime())) return false;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const days = parseInt(endingInFilter, 10);
          const targetDate = new Date(today);
          targetDate.setDate(today.getDate() + days);
          return endDate >= today && endDate <= targetDate;
        } catch (error) {
          return false;
        }
      })();
      const matchesProjectTypeFilter =
        projectTypeFilter === 'All' ||
        (projectTypeFilter === 'Survey Projects' && row.ProjectType !== 'ruddr') ||
        (projectTypeFilter === 'Ruddr Projects' && row.ProjectType === 'ruddr');
      return matchesSearch && matchesStatus && matchesSurveyResponse && matchesType && matchesPractice && matchesEndingIn && matchesProjectTypeFilter;
    });
  }, [projects, statusMap, typeMap, searchQuery, statusFilter, surveyResponseFilter, typeFilter, practiceFilter, endingInFilter, projectTypeFilter]);

  useEffect(() => {
    const types = Array.from(new Set(projects.map((p) => {
      const typeKey = p.Type ? p.Type.toString().toLowerCase() : '';
      return typeMap[typeKey] || typeMap[p.Type] || p.Type;
    }).filter(Boolean)));
    setDistinctTypes(types);

    const practiceNames = Array.from(new Set(projects.map((p) => p.PracticeName || p.Practice).filter(Boolean)));
    setDistinctPractices(practiceNames);
  }, [projects, typeMap]);
  function requestSort(key) {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  }

  const sortedSurveys = useMemo(() => {
    let surveys;
    if (activePopup === 'completed') {
      surveys = [...filteredCompletedSurveys];
    } else {
      surveys = [...filteredSurveys];
    }
    if (sortConfig.key) {
      surveys.sort((a, b) => {
        let aValue = a[sortConfig.key] || '';
        let bValue = b[sortConfig.key] || '';

        // Handle specific data types
        if (sortConfig.key === 'sentDate' || sortConfig.key === 'date') {
          aValue = aValue && aValue !== 'N/A' ? new Date(aValue.split('/').join('-')) : new Date(0);
          bValue = bValue && bValue !== 'N/A' ? new Date(bValue.split('/').join('-')) : new Date(0);
        } else if (sortConfig.key === 'csat') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else {
          aValue = aValue.toString().toLowerCase();
          bValue = bValue.toString().toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return surveys;
  }, [activePopup, filteredCompletedSurveys, filteredSurveys, sortConfig]);

  const totalRows = sortedSurveys.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / (rowsPerPageSurveys || 10)));
  const { startPage, endPage } = getPageRange(page, totalPages);

  // Reset page to 1 when activePopup changes to avoid pagination issues
  useEffect(() => {
    setPage(1);
  }, [activePopup]);

  // Validate page number when totalPages changes
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  const handlePrevious = () => {
    setPage((prev) => Math.max(1, prev - 1));
  };

  const handleNext = () => {
    setPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handleRowsPerPageChange = (e) => {
    const newRowsPerPage = Number(e.target.value);
    setRowsPerPageSurveys(newRowsPerPage);
    setPage(1);
  };

  const indexOfLastRow = page * rowsPerPageSurveys;
  const indexOfFirstRow = indexOfLastRow - rowsPerPageSurveys;
  const currentRows = sortedSurveys.slice(indexOfFirstRow, indexOfLastRow);

  const handleViewSurveys = () => {
    if (transitioning) return;
    setActivePopup('surveys');
    setSelectedNotRespondedRows([]);
    setPage(1);
  };

  const handleViewSurveysCompleted = () => {
    if (transitioning) return;
    setActivePopup('completed');
    setPage(1);
  };

  const handleCloseActivePopup = () => {
    setActivePopup(null);
    setSurveyStatusFilter('');
    setSurveyProjectFilter('');
    setSurveyNameFilter('');
    setSurveyAccountFilter('');
    setCsatFilter('');
    setPage(1);
    setSortConfig({ key: null, direction: 'asc' });
    setSelectedNotRespondedRows([]);
  };

  const handleNavigatePopup = (direction) => {
    if (transitioning) return;

    setTransitioning(true);
    setSlideDirection(direction === 'next' ? 'right' : 'left');

    const popupOrder = ['surveys', 'completed'];
    const currentIndex = popupOrder.indexOf(activePopup);
    let newIndex;

    if (direction === 'next') {
      newIndex = (currentIndex + 1) % popupOrder.length;
    } else {
      newIndex = (currentIndex - 1 + popupOrder.length) % popupOrder.length;
    }

    setSurveyStatusFilter('');
    setSurveyProjectFilter('');
    setSurveyNameFilter('');
    setSurveyAccountFilter('');
    setCsatFilter('');
    setPage(1);
    setSortConfig({ key: null, direction: 'asc' });
    setSelectedNotRespondedRows([]);

    setTimeout(() => {
      setActivePopup(popupOrder[newIndex]);
      setTransitioning(false);
    }, 300);
  };

  const handleOpenEditDialog = useCallback((project) => {
    const latestProject = projects.find(p => p.ID === project.id) || project;
    setSelectedSurvey({
      ...latestProject,
      id: latestProject.ID,
      'Project Name': latestProject.Name || 'N/A',
      Account: latestProject.Account || 'N/A',
      'Start Date': latestProject.GoLiveDate
        ? new Date(latestProject.GoLiveDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : 'N/A',
      'End Date': latestProject.EndDate
        ? new Date(latestProject.EndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : 'N/A',
      Status: latestProject.Status || 'N/A',
      Type: latestProject.Type || 'N/A',
      Client: latestProject.Client || 'N/A',
      Description: latestProject.Description || 'N/A',
      'Created By': latestProject.CreatedBy || 'N/A',
      'Response Received?': latestProject.SurveyResponse,
      'Response Date': latestProject.ResponseDate
        ? new Date(latestProject.ResponseDate).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
        : 'N/A',
      FullName: latestProject.FullName || '',
      Email: latestProject.Email || '',
      CustomerContactIDs: latestProject.CustomerContactIDs || '',
    });
    setShowEditDialog(true);
  }, [projects]);

  const handleDeleteClick = useCallback((project) => {
    setSelectedSurvey(project);
    setShowDeleteDialog(true);
  }, []);

  const handleAddContacts = (project) => {
    setSelectedProjectForContacts(project);
    setShowRuddrContactsDialog(true);
  };

  const handleSendClick = useCallback((project) => {
    setSelectedSurvey(project);
    setShowSendDialog(true);
  }, []);

  const updateProjectAfterEdit = (prevProjects, updatedData, optimisticData) => {
    const updatedProjects = prevProjects.map((p) => {
      if (p.ID === updatedData.project_id) {
        const updatedFields = updatedData.updated_fields;
        let formattedStartDate = null;
        let formattedEndDate = null;
        if (updatedFields.project_start_date) {
          try {
            formattedStartDate = updatedFields.project_start_date;
          } catch (error) {
            console.warn('Invalid date format:', updatedFields.project_start_date);
          }
        }
        if (updatedFields.endDate) {
          try {
            formattedEndDate = updatedFields.endDate;
          } catch (error) {
            console.warn('Invalid date format:', updatedFields.endDate);
          }
        }

        const fullNames = updatedFields.project_customer_contacts
          .map((contact) => contact.full_name)
          .join(', ');
        const emails = updatedFields.project_customer_contacts
          .map((contact) => contact.email)
          .join(', ');
        const contactIds = updatedFields.project_customer_contacts
          .map((contact) => contact.id)
          .join(', ');

        return {
          ...p,
          Name: optimisticData.projectName || p.Name,
          Status: optimisticData.projectStatus || p.Status,
          Type: optimisticData.projectType || p.Type,
          Description: optimisticData.projectDescription || p.Description,
          GoLiveDate: formattedStartDate || p.GoLiveDate,
          EndDate: formattedEndDate || p.EndDate,
          Account: p.Account,
          FullName: fullNames || p.FullName,
          Email: emails || p.Email,
          CustomerContactIDs: contactIds || p.CustomerContactIDs,
          CreatedBy: p.CreatedBy,
          SurveyResponse: p.SurveyResponse,
          ResponseDate: p.ResponseDate,
        };
      }
      return p;
    });
    localStorage.setItem('cachedProjects', JSON.stringify(updatedProjects));
    return updatedProjects;
  };

  const handleSaveProject = async (updatedData, optimisticData) => {
    try {
      await apiService.putData('projects', updatedData, accessToken);
      const userId = sessionStorage.getItem('user_id');
      const PracticeId = sessionStorage.getItem('practice_id');
      let projectTypeParam = 'all';
      if (projectTypeFilter === 'Survey Projects') {
        projectTypeParam = 'survey';
      } else if (projectTypeFilter === 'Ruddr Projects') {
        projectTypeParam = 'ruddr';
      }
      const projectDataResponse = await apiService.fetchData(`projects/${userId}/${PracticeId}/${projectTypeParam}`);
      const projectContainer = projectDataResponse?.data || projectDataResponse;
      let projectList = [];
      if (Array.isArray(projectContainer?.results)) {
        projectList = projectContainer.results;
      } else if (Array.isArray(projectContainer)) {
        projectList = projectContainer;
      }
      setProjects(projectList);
      localStorage.setItem('cachedProjects', JSON.stringify(projectList));
      setSnackbarMessage('Project updated successfully.');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(`Failed to update project: ${error.response?.data?.message || error.message}`);
      setSnackbarOpen(true);
    }
  };

  const handleConfirmDelete = useCallback(async (project) => {
    setShowDeleteDialog(false);
    setSelectedSurvey(null);
    try {
      await apiService.deleteData('projects', { ID: project.id }, accessToken);
      const userId = sessionStorage.getItem('user_id');
      const PracticeId = sessionStorage.getItem('practice_id');
      let projectTypeParam = 'all';
      if (projectTypeFilter === 'Survey Projects') {
        projectTypeParam = 'survey';
      } else if (projectTypeFilter === 'Ruddr Projects') {
        projectTypeParam = 'ruddr';
      }
      const projectDataResponse = await apiService.fetchData(`projects/${userId}/${PracticeId}/${projectTypeParam}`);
      const projectContainer = projectDataResponse?.data || projectDataResponse;
      let projectList = [];
      if (Array.isArray(projectContainer?.results)) {
        projectList = projectContainer.results;
      } else if (Array.isArray(projectContainer)) {
        projectList = projectContainer;
      }
      setProjects(projectList);
      localStorage.setItem('cachedProjects', JSON.stringify(projectList));
      setSnackbarMessage('Project deleted successfully.');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(`Failed to delete project: ${error.response?.data?.message || error.message}`);
      setSnackbarOpen(true);
    }
  }, [accessToken, projectTypeFilter]);

  const handleSaveSend = (data) => {
    setShowSendDialog(false);
    setSelectedSurvey(null);
    if (data) {
      // Update surveys and statistics states with the new data
      setSurveys(data.updatedSurveys || surveys);
      setStatistics(data.updatedStatistics || statistics);
      localStorage.setItem('cachedSurveys', JSON.stringify(data.updatedSurveys || surveys));
      localStorage.setItem('cachedStatistics', JSON.stringify(data.updatedStatistics || statistics));
    }
    setSnackbarMessage('Survey sent successfully.');
    setSnackbarOpen(true);
  };

  // Helper function to get headers and data for Excel export
  function getExportData(activePopup, sortedSurveys, calculateDaysSinceSent) {
    let headers = [];
    let data = [];

    switch (activePopup) {
      case 'completed':
        headers = ['Survey Name', 'Client', 'Project', 'Customer Details', 'Responded Date', 'CSAT'];
        data = sortedSurveys.map((row) => ({
          'Survey Name': row.surveyName || 'N/A',
          'Client': row.client || 'N/A',
          'Project': row.project || 'N/A',
          'Customer Details': row.account || 'N/A',
          'Responded Date': row.date || 'N/A',
          'CSAT': row.csat || 'N/A',
        }));
        break;
      case 'csat':
        headers = ['Client', 'Project', 'Customers', 'Date', 'CSAT'];
        data = sortedSurveys.map((row) => ({
          'Client': row.client || 'N/A',
          'Project': row.project || 'N/A',
          'Customers': row.account || 'N/A',
          'Date': row.date || 'N/A',
          'CSAT': row.csat || 'N/A',
        }));
        break;
      case 'risk':
        headers = ['Project', 'Account Executive', 'Start Date', 'Risk Factor'];
        data = sortedSurveys.map((row) => ({
          'Project': row.project || 'N/A',
          'Account Executive': row.account || 'N/A',
          'Start Date': row.date || 'N/A',
          'Risk Factor': row.riskFactor || 'N/A',
        }));
        break;
      default:
        headers = ['Survey Name', 'Client', 'Project', 'Customer Details', 'Sent Date', 'Responded Date', 'Days Ageing', 'Mail Stats'];
        data = sortedSurveys.map((row) => ({
          'Survey Name': row.surveyName || 'N/A',
          'Client': row.client || 'N/A',
          'Project': row.project || 'N/A',
          'Customer Details': row.account || 'N/A',
          'Sent Date': row.sentDate || 'N/A',
          'Responded Date': row.RespondedDate || 'N/A',
          'Days Ageing': calculateDaysDiff(row.sentDate, row.RespondedDate),
          'Mail Stats': row.counter || 'N/A',
        }));
        break;
    }

    return { headers, data };
  }

  // Helper function to get email content and file name
  function getEmailContent(activePopup) {
    const titles = {
      csat: 'CSAT Surveys',
      completed: 'Completed Surveys',
      default: 'Surveys Sent',
    };
    const title = titles[activePopup] || titles.default;
    const fileName = `${title.toLowerCase().replace(/\s+/g, '-')}.xlsx`;
    const subject = encodeURIComponent(`${title} Data - Internal Sharing`);
    const body = encodeURIComponent(
      `Hello,\n\nPlease find the ${title} data attached in the Excel file. The file has been downloaded to your device. Please attach it to this email and send it to the relevant team members.\n\nNote: Ensure this data is shared only within the company.\n\nBest regards,\n[Your Name]`
    );
    return { fileName, subject, body };
  }

  const handleExportToCSV = () => {
    if (sortedSurveys.length === 0) {
      setSnackbarMessage('No data to share.');
      setSnackbarOpen(true);
      return;
    }

    const { headers, data } = getExportData(activePopup, sortedSurveys, calculateDaysSinceSent);
    const { fileName, subject, body } = getEmailContent(activePopup);

    // Create a worksheet from the data
    const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });

    // Create a workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    // Generate Excel file and trigger download
    XLSX.writeFile(workbook, fileName);

    // Open email client with pre-filled subject and body
    const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;

    setSnackbarMessage(
      `Excel file downloaded. Your email client has been opened. Please attach the '${fileName}' file and send to internal employees only.`
    );
    setSnackbarOpen(true);
  };
  const handleSelectAllNotResponded = (event) => {
    if (event.target.checked) {
      const notRespondedRows = currentRows
        .filter((row) => row.responseStatus === 'Not Responded')
        .map((row) => row.id);
      setSelectedNotRespondedRows(notRespondedRows);
    } else {
      setSelectedNotRespondedRows([]);
    }
  };

  const handleSelectRow = (rowId) => {
    if (selectedNotRespondedRows.includes(rowId)) {
      setSelectedNotRespondedRows(selectedNotRespondedRows.filter((id) => id !== rowId));
    } else {
      setSelectedNotRespondedRows([...selectedNotRespondedRows, rowId]);
    }
  };

  const handleSendSurveys = () => {
    const selectedSurveys = filteredSurveys.filter(survey =>
      selectedNotRespondedRows.includes(survey.id) &&
      survey.responseStatus === 'Not Responded'
    );
    if (selectedSurveys.length === 0) {
      setSnackbarMessage('No valid surveys selected for resending.');
      setSnackbarOpen(true);
      return;
    }
    setResendSurveys(selectedSurveys);
    setShowResendDialog(true);
  };

  const handleResendSave = async (resendData) => {
    try {
      // Validate resendData
      if (!Array.isArray(resendData) || resendData.length === 0) {
        setSnackbarMessage('No valid data provided for resending surveys.');
        setSnackbarOpen(true);
        return;
      }

      // Process each survey to be resent
      for (const data of resendData) {
        const { contactName, email, contactId, uniqueId, surveyId, surveyType, projectName, projectId, subject, body, sentBy } = data;

        // Validate required fields
        if (!email || !contactId || !uniqueId || !surveyId) {
          console.error(`Invalid data for contact: ${contactName}`);
          continue;
        }

        const emailPayload = {
          endpoint: 'send_email',
          customerName: contactName,
          surveyLink: body, // Use the provided body which contains the survey link
          surveyType: surveyType,
          projectName: projectName,
          send_to: email,
          subject: subject,
          token: accessToken,
          templateType: 'survey',
        };

        const surveyPayload = {
          SurveyID: surveyId,
          ProjectID: projectId,
          ContactID: parseInt(contactId),
          SentBy: sentBy,
          Subject: subject,
          Body: body,
          EmailSent: 0,
          UniqueID: uniqueId,
        };

        try {
          // Send email
          const emailResponse = await apiService.sendEmail(
            emailPayload.endpoint,
            emailPayload.customerName,
            emailPayload.surveyLink,
            emailPayload.surveyType,
            emailPayload.projectName,
            emailPayload.send_to,
            emailPayload.subject,
            emailPayload.token,
            emailPayload.templateType
          );
          console.log(`Email sent to ${contactName}:`, emailResponse);
          surveyPayload.EmailSent = 1;
        } catch (emailError) {
          console.error(`Error sending email to ${contactName}:`, emailError);
        }

        // Update survey data
        try {
          const surveyResponse = await apiService.putData('send_survey', surveyPayload, accessToken);
          console.log(`Survey updated for ${contactName}:`, surveyResponse);
        } catch (surveyError) {
          console.error(`Error updating survey for ${contactName}:`, surveyError);
        }
      }

      // Clear selections and close dialog
      setSelectedNotRespondedRows([]);
      setShowResendDialog(false);
      setResendSurveys([]);
      setSnackbarMessage('Surveys resent successfully.');
      setSnackbarOpen(true);

      // Refresh survey data
      await fetchData();
    } catch (error) {
      console.error('Error resending surveys:', error);
      setSnackbarMessage('Failed to resend surveys.');
      setSnackbarOpen(true);
    }
  };

  const notRespondedCount = currentRows.filter((row) => row.responseStatus === 'Not Responded').length;
  const allNotRespondedSelected = selectedNotRespondedRows.length === notRespondedCount && notRespondedCount > 0;

  const getViewButtonClickHandler = (label) => {
    if (label === 'Surveys sent') return handleViewSurveys;
    if (label === 'Surveys Completed') return handleViewSurveysCompleted;
    return undefined;
  };

  const getPopupTitle = () => {
    switch (activePopup) {
      case 'surveys':
        return 'Surveys Sent';
      case 'completed':
        return 'Completed Surveys';
      case 'csat':
        return 'CSAT Summary';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: '#ffffff', // Changed to white
          color: '#333333',
        }}
      >
        <Box sx={{ width: '50%', maxWidth: '300px', position: 'relative' }}> {/* Reduced width and maxWidth */}
          <LinearProgress
            className="Project-tech-loading-bar"
            sx={{
              height: 10, // Slightly reduced height for compactness
              borderRadius: 5,
              backgroundColor: '#e0e0e0', // Lighter gray for contrast on white background
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(1.62deg, #034589 -6.8%, #0066D1 107.85%);',
              },
            }}
          />
          <Box className="Project-circuit-overlay" />
        </Box>
        <Typography
          sx={{
            mt: 2,
            fontFamily: "'Mulish', sans-serif",
            fontSize: '1.2rem',
            fontWeight: 500,
            background: 'linear-gradient(1.62deg, #034589 -6.8%, #0066D1 107.85%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 4px rgba(0, 102, 209, 0.2)',
          }}
        >
          Getting everything ready...
        </Typography>
      </Box>
    );
  }
  const hasLimitedAccess = Object.values(moduleAccess).includes('None');
  return (
    <ErrorBoundary>
      <div className="Project-proj-page">
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: '100vw', overflowX: 'hidden' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" className="Project-titleproject">
              Projects
            </Typography>
            {hasWriteAccess && (
              <Typography className="Project-add-new-project" onClick={() => setOpenDialog(true)}>
                <img src={unionSvg} alt="Add" className="Project-add-icon" /> Add New Project
              </Typography>
            )}
          </Box>
          {/* New project summary cards */}
          {moduleAccess.Projects !== 'Read-only' && (
            <Box className="Project-card-container" key={`${statusFilter}-${surveyResponseFilter}-${typeFilter}-${practiceFilter}-${endingInFilter}`}>
              {/* Card 1: Total Projects */}
              <div className="Project-stat-card Project-total-projects">
                <div className="Project-stat-content">
                  <div className="Project-stat-text">
                    <Typography className="Project-stat-label">Total Projects</Typography>
                    <div className="Project-stat-value-with-icon">
                      <span className="Project-stat-icon-circle Project-icon-blue">
                        <span className="Project-stat-icon-dot" />
                      </span>
                      <Typography className="Project-stat-value">{filteredProjects.length}</Typography>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: Projects with Survey Sent */}
              <div className="Project-stat-card Project-survey-sent">
                <div className="Project-stat-content">
                  <div className="Project-stat-text">
                    <Typography className="Project-stat-label">Projects with Survey Sent</Typography>
                    <div className="Project-stat-value-with-icon">
                      <span className="Project-stat-icon-circle Project-icon-green">
                        <span className="Project-stat-icon-dot" />
                      </span>
                      <Typography className="Project-stat-value">
                        {filteredProjects.filter((p) => p['Survey Sent?'] === 'Yes').length}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Projects with Response Received */}
              <div className="Project-stat-card Project-response-received">
                <div className="Project-stat-content">
                  <div className="Project-stat-text">
                    <Typography className="Project-stat-label">Projects with Response Recieved</Typography>
                    <div className="Project-stat-value-with-icon">
                      <span className="Project-stat-icon-circle Project-icon-orange">
                        <span className="Project-stat-icon-dot" />
                      </span>
                      <Typography className="Project-stat-value">
                        {filteredProjects.filter((p) => p['Response Received?'] === 'Yes').length}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </Box>
          )}
          <Box className="Project-filters-container">
            <div className="Project-filter-box">
              <select
                className="Project-form-control Project-filter-select"
                id="drpStatus"
                name="ProjectParam.StatusId"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div className="Project-filter-box2">
              <select
                className="Project-filter-select"
                value={surveyResponseFilter}
                onChange={(e) => setSurveyResponseFilter(e.target.value)}
              >
                <option value="">Survey Response Status</option>
                <option value="Yes">Received</option>
                <option value="No">Not Received</option>
              </select>
            </div>
            <div className="Project-filter-box2">
              <select
                className="Project-filter-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                {distinctTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="Project-filter-box3">
              <select
                className="Project-filter-select"
                value={projectTypeFilter}
                onChange={(e) => setProjectTypeFilter(e.target.value)}
              >
                <option value="All">All</option>
                <option value="Survey Projects">Other Projects</option>
                <option value="Ruddr Projects">Ruddr Projects</option>
              </select>
            </div>
            <div className="Project-filter-box-days">
              <select
                className="Project-filter-select"
                value={endingInFilter}
                onChange={(e) => setEndingInFilter(e.target.value)}
              >
                <option value="">Projects Ending In</option>
                <option value="7">7 Days</option>
                <option value="15">15 Days</option>
                <option value="30">30 Days</option>
                <option value="60">60 Days</option>
              </select>
            </div>
          </Box>
          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <LinearProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Loading projects...
              </Typography>
            </Box>
          ) : (
            <ProjectTable
              projects={filteredProjects}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              projectTypeFilter={projectTypeFilter}
              endingInFilter={endingInFilter}
              practiceFilter={practiceFilter}
              setPracticeFilter={setPracticeFilter}
              distinctPractices={distinctPractices}
              tableLoading={tableLoading}
              onEditClick={handleOpenEditDialog}
              onAddContacts={handleAddContacts}
              onSaveClick={async (updatedData, optimisticData) => {
                const updatedProjects = projects.map((p) => {
                  if (p.ID === updatedData.project_id) {
                    const updatedFields = updatedData.updated_fields;
                    const fullNames = updatedFields.project_customer_contacts
                      .map((contact) => contact.full_name)
                      .join(', ');
                    const emails = updatedFields.project_customer_contacts
                      .map((contact) => contact.email)
                      .join(', ');
                    const customerContactIds = updatedFields.project_customer_contacts
                      .map((contact) => contact.id)
                      .join(', ');
                    return {
                      ...p,
                      Name: optimisticData.projectName || p.Name,
                      Status: optimisticData.projectStatus || p.Status,
                      Type: optimisticData.projectType || p.Type,
                      GoLiveDate: updatedFields.project_start_date || p.GoLiveDate,
                      EndDate: updatedFields.endDate || p.EndDate,
                      Description: optimisticData.projectDescription || p.Description,
                      FullName: fullNames || p.FullName,
                      Email: emails || p.Email,
                      CustomerContactIDs: customerContactIds || p.CustomerContactIDs,
                    };
                  }
                  return p;
                });
                setProjects(updatedProjects);
                localStorage.setItem('cachedProjects', JSON.stringify(updatedProjects));
                setSnackbarMessage('Project updated successfully.');
                setSnackbarOpen(true);
              }}
              onDeleteClick={handleDeleteClick}
              onSendClick={handleSendClick}
              setSnackbarMessage={setSnackbarMessage}
              setSnackbarOpen={setSnackbarOpen}
              surveys={apiSurveys}
            />
          )}

          <AddProjectDialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            onSave={handleAddProject}
            projectStatuses={statuses}
            projectTypes={types}
            practices={practices} // Add this line
          />

          {activePopup && (
            <Dialog
              open={!!activePopup}
              onClose={handleCloseActivePopup}
              maxWidth="md"
              fullWidth
              classes={{ paper: 'Project-dialog-paper' }}
              BackdropProps={{
                className: 'Project-dialog-backdrop',
              }}
            >
              <DialogTitle>
                <Typography variant="h6">{getPopupTitle()}</Typography>
                <button className="Project-close-btn" onClick={handleCloseActivePopup}>
                  ×
                </button>
              </DialogTitle>

              <DialogContent className={`Project-dialog-content ${slideDirection}`}>
                {popupDataLoading ? (
                  <div className="Project-loading-message">
                    <CircularProgress />
                    {/* <Typography variant="h6" sx={{ mt: 2, color: '#013E7D', fontWeight: 'bold' }}>
                      Loading data...
                    </Typography> */}
                  </div>
                ) : (
                  <>
                    {activePopup === 'csat' ? (
                      <Box className="Project-csat-filter-results-container">
                        <Box className="Project-table-header Project-csat-table-header">
                          <Box className="Project-left-header"></Box>
                          <Box className="Project-right-header">
                            <Box className="Project-pagination-info-container">
                              <span className="Project-pagination-info">
                                Results: {Math.min(indexOfFirstRow + 1, totalRows)} - {Math.min(indexOfLastRow, totalRows)} of {totalRows}
                              </span>
                              <Box>
                                <div className="Project-entries-box">
                                  <select
                                    className="Project-entries-select"
                                    value={rowsPerPageSurveys}
                                    onChange={handleRowsPerPageChange}
                                  >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                  </select>
                                </div>
                              </Box>
                            </Box>

                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <SurveyFilters
                        surveys={activePopup === 'completed' ? completedSurveys : surveys}
                        surveyStatusFilter={surveyStatusFilter}
                        surveyProjectFilter={surveyProjectFilter}
                        surveyNameFilter={surveyNameFilter}
                        surveyAccountFilter={surveyAccountFilter}
                        surveyAgeingFilter={surveyAgeingFilter}
                        onStatusChange={activePopup === 'surveys' ? setSurveyStatusFilter : null}
                        onProjectChange={setSurveyProjectFilter}
                        onNameChange={setSurveyNameFilter}
                        onAccountChange={setSurveyAccountFilter}
                        onAgeingChange={activePopup === 'surveys' ? setSurveyAgeingFilter : () => { }}
                        calculateDaysDiff={calculateDaysDiff}
                        showStatus={activePopup === 'surveys'}
                        showAgeing={activePopup === 'surveys'}
                        showCsatFilter={activePopup === 'completed'}
                        onCsatChange={setCsatFilter}
                        surveyCsatFilter={csatFilter} // Pass csatFilter as surveyCsatFilter
                      />
                    )}
                    {activePopup === 'surveys' && Array.isArray(filteredSurveys) && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Box sx={{ flex: 1, height: 200 }}>
                          <Pie data={pieChartData} options={pieChartOptions} />
                        </Box>
                        <Box sx={{ flex: 1, height: 200 }}>
                          <Bar data={barChartData} options={barChartOptions} />
                        </Box>
                      </Box>
                    )}
                    {(activePopup === 'surveys' || activePopup === 'completed' || activePopup === 'risk') && Array.isArray(currentRows) && (
                      <div className="Project-table-header">
                        <div className="Project-left-header">
                        </div>
                        <div className="Project-right-header">
                          <div className="Project-pagination-info-container">
                            <span className="Project-pagination-info">
                              Results: {Math.min(indexOfFirstRow + 1, totalRows)} - {Math.min(indexOfLastRow, totalRows)} of {totalRows}
                            </span>
                            <div className="Project-entries-box">
                              <select
                                className="Project-entries-select"
                                value={rowsPerPageSurveys}
                                onChange={handleRowsPerPageChange}
                              >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {activePopup === 'completed' && Array.isArray(currentRows) ? (
                      <CompletedSurveyTable
                        currentRows={currentRows}
                        sortConfig={sortConfig}
                        onSort={requestSort}
                        csatPercentages={csatPercentages}
                        onSurveyClick={handleSurveyClick}
                      />
                    ) : Array.isArray(currentRows) ? (
                      <SurveyTable
                        currentRows={currentRows}
                        surveyStatusFilter={surveyStatusFilter}
                        selectedNotRespondedRows={selectedNotRespondedRows}
                        sortConfig={sortConfig}
                        onSort={requestSort}
                        onSelectRow={handleSelectRow}
                        onSelectAll={handleSelectAllNotResponded}
                        allNotRespondedSelected={allNotRespondedSelected}
                        notRespondedCount={notRespondedCount}
                      />
                    ) : (
                      <Typography>No data available.</Typography>
                    )}
                    {(activePopup === 'surveys' || activePopup === 'completed') && (
                      <div className="Project-table-footer">
                        <Pagination
                          totalPages={totalPages}
                          currentPage={page}
                          onPageChange={setPage}
                          startPage={startPage}
                          endPage={endPage}
                          onPrevious={handlePrevious}
                          onNext={handleNext}
                        />
                      </div>
                    )}
                    <div className="Project-surveys-actions">
                      <button onClick={handleCloseActivePopup} className="Project-cancel-btn">
                        Cancel
                      </button>
                      {activePopup === 'surveys' && surveyStatusFilter === 'Not Responded' && selectedNotRespondedRows.length > 0 && (
                        filteredSurveys.some(survey =>
                          selectedNotRespondedRows.includes(survey.id) &&
                          survey.counter < 3 &&
                          survey.responseStatus === 'Not Responded'
                        ) && (
                          <button onClick={handleSendSurveys} className="Project-show-btn">
                            Send Survey
                          </button>
                        )
                      )}
                      {hasWriteAccess && (
                        <button onClick={handleExportToCSV} className="Project-show-btn">
                          {activePopup === 'completed' || activePopup === 'csat' || activePopup === 'risk' ? 'Export' : 'Share'}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          )}
          <ViewResponsePopup
            open={!!selectedSurveyId && !!selectedClientId}
            onClose={handleClosePopup}
            surveyId={selectedSurveyId}
            clientId={selectedClientId}
          />
          {activePopup && (
            <div className="Project-dialog-nav-container">
              <button
                className="Project-dialog-nav-button Project-dialog-nav-left"
                onClick={() => handleNavigatePopup('prev')}
                aria-label="Navigate to Previous Popup"
                disabled={transitioning}
              >
                <ArrowLeftIcon fontSize="large" />
              </button>
              <button
                className="Project-dialog-nav-button Project-dialog-nav-right"
                onClick={() => handleNavigatePopup('next')}
                aria-label="Navigate to Next Popup"
                disabled={transitioning}
              >
                <ArrowRightIcon fontSize="large" />
              </button>
            </div>
          )}

          {showEditDialog && !loading && statuses.length > 0 && types.length > 0 && selectedSurvey && (
            <EditDialog
              project={selectedSurvey}
              statuses={statuses}
              types={types}
              onClose={() => {
                setShowEditDialog(false);
                setSelectedSurvey(null);
              }}
              onSave={handleSaveProject}
            />
          )}


          {showDeleteDialog && selectedSurvey && (
            <DeleteDialog
              project={selectedSurvey}
              onClose={() => {
                setShowDeleteDialog(false);
                setSelectedSurvey(null);
              }}
              onConfirm={handleConfirmDelete}
            />
          )}
          {showSendDialog && selectedSurvey && (
            <SendDialog
              project={selectedSurvey}
              contacts={selectedSurvey.FullName ? selectedSurvey.FullName.split(', ').filter(name => name.trim()) : []}
              emails={selectedSurvey.Email ? selectedSurvey.Email.split(', ').filter(email => email.trim()) : []}
              customerContactIds={selectedSurvey.CustomerContactIDs ? selectedSurvey.CustomerContactIDs.split(',').map(id => id.trim()).filter(id => id) : []}
              surveys={apiSurveys}
              onClose={() => {
                setShowSendDialog(false);
                setSelectedSurvey(null);
              }}
              onSave={handleSaveSend}
            />
          )}
          {showResendDialog && resendSurveys.length > 0 && (
            <ResendDialog
              project={{ 'Project Name': resendSurveys[0].project, id: resendSurveys[0].projectId }}
              contacts={resendSurveys.map(s => s.account).filter(Boolean)}
              emails={resendSurveys.map(s => s.email).filter(Boolean)}
              customerContactIds={resendSurveys.map(s => s.contactId).filter(Boolean)}
              surveys={apiSurveys}
              surveyIds={resendSurveys.map(s => s.surveyId).filter(Boolean)}
              uniqueIds={resendSurveys.map(s => s.UniqueID).filter(Boolean)}
              onClose={() => {
                setShowResendDialog(false);
                setResendSurveys([]);
              }}
              onSave={handleResendSave}
            />
          )}

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={5000}
            onClose={() => {
              setSnackbarOpen(false);
            }}
            message={snackbarMessage || 'An error occurred.'}
            sx={{ '& .MuiSnackbarContent-root': { backgroundColor: snackbarMessage.includes('successfully') ? '#4caf50' : '#f44336' } }}
          />
        </Box>
      </div>
    </ErrorBoundary>
  );
};

export default Projects;